// 양식 저작 경고의 **집계** 층 — "이 문서를 지금 저장하면 무엇이 어긋난 채로 나가나".
//
// 문제의식(줄기 composer-system-query-terminology P5): 지금 경고들은 **그 패널을 열어야만** 보인다.
// 조회 파라미터 경고는 캔버스 빈 곳을 클릭해 속성 패널을 스크롤해야 하고, 변수 경고는 변수 편집기
// 안에 있다. 저작자는 문제를 안고도 모른 채 저장·배포할 수 있다. 그래서 각 패널이 이미 내고 있는
// 신호를 **한 자리에서 세어** 저장 버튼 옆 배지로 올린다.
//
// ⚠️ 이 파일은 **판정하지 않는다 — 모은다.** 각 신호의 정의는 이미 있는 곳(catalogParamBindings의
// 행 산출·collectCatalogConsumers의 소비처 열거·variableRefs의 참조 조망·keyValidation의 키 형식)이
// 정본이고, 여기서 규칙을 다시 쓰면 배지와 패널이 서로 다른 말을 하게 된다. 예외는 식별 키 중복
// 하나뿐인데, 그 사유는 collectBindingKeyDuplicates JSDoc에 적었다.
//
// 심각도는 **전부 경고**다. 저장을 막지 않는 현행 정책을 유지한다 — 미완성 양식을 저장해 두고
// 이어서 저작하는 것이 정상 흐름이고, 차단으로 바꾸면 그 흐름이 끊긴다.

import type {
  CatalogEntry,
  ComposerNode,
  ReportTemplate,
  TableNode,
} from '@safetyhub/shared';
import { withObjectParticle, withSubjectParticle, withTopicParticle } from '@safetyhub/shared';
import { deriveWriteForm, findOversizeUnits, walkBindings } from '@safetyhub/report-composer';
import type { OversizeFinding } from '@safetyhub/report-composer';

import {
  buildCatalogParamRows,
  collectCatalogConsumers,
  type CatalogParamRow,
} from './catalogParamBindings';
import { flatKeyError } from './keyValidation';
// 에디터 높이 입력란과 같은 단위 상수를 쓴다 — 병기 mm가 입력란 눈금과 어긋나면 병기가 무의미하다.
import { MM_TO_PX } from './sampleTemplate';
// 반복 행 필드 목록 — 저작 UI의 "반복 항목 필드" 후보와 **같은 파생**을 쓴다(행 스코프 판정 재료).
import { collectRepeatSources, findRepeatSource, type RepeatSource } from './repeatSources';
// 분기 슬롯 경로 — 속성 패널의 중복 경고 제외(makeMutualExclusionCheck)와 **같은 재료**를 쓴다.
// 배지가 자기 순회를 또 만들면 패널과 배지가 서로 다른 자리를 세게 된다.
import { collectOwnerSlotPaths, type BranchSlotStep } from './sharedKeyHighlight';
import {
  collectVariableConsumers,
  variableRefTotal,
  type VariableConsumerRef,
} from './variableRefs';

/**
 * 경고 종류 — 배지 내역의 묶음 축이자 클릭 이동의 목적지 축.
 *
 * 이름은 **어디를 고쳐야 하나**로 갈랐다(`param-*`=조회 파라미터 지정, `variable-*`=양식 전역 변수,
 * `binding-*`=본문 바인딩). 저작자가 배지에서 읽는 것은 "무슨 규칙을 어겼나"가 아니라 "어느 편집기를
 * 열어야 하나"이기 때문이다.
 */
export type TemplateIssueKind =
  | 'param-missing-required'
  | 'param-empty-binding'
  | 'param-dangling-variable'
  | 'param-unknown'
  | 'catalog-unknown-entry'
  | 'catalog-orphan-binding'
  | 'variable-unused'
  | 'variable-key-duplicate'
  | 'variable-key-invalid'
  | 'binding-key-duplicate'
  | 'binding-key-shared-intent-gap'
  | 'write-tree-missing'
  | 'layout-overflow-certain'
  | 'layout-overflow-possible';

/** 경고가 가리키는 편집기 — 배지 클릭 시 어디로 데려갈지. */
export type TemplateIssueTarget =
  | 'catalog-params'
  | 'variables'
  | 'canvas'
  /** 작성 탭 캔버스(`ViewMode='writeForm'`) — 작성 트리 자체가 없는 경고의 목적지. */
  | 'write-canvas';

/** 경고 한 건. `severity`가 'warn' 고정인 것은 정책이다(위 파일 주석 참조). */
export interface TemplateIssue {
  kind: TemplateIssueKind;
  severity: 'warn';
  /** 저작자가 읽는 한 줄 — 무엇이 어디서 어긋났는지. */
  label: string;
  /** 이 경고를 고칠 편집기. */
  target: TemplateIssueTarget;
  catalogKey?: string;
  paramName?: string;
  variableKey?: string;
  bindingKey?: string;
  /** target==='canvas'일 때 선택할 노드 id(셀 경고면 그 셀이 속한 표 노드). */
  nodeId?: string;
  /**
   * 이 경고의 **처방 대상 이미지 노드**들(넘침 경고 전용).
   *
   * `nodeId`(넘치는 단위의 소유자 = 보통 반복 노드)와 별개로 싣는 이유는, 저작자가 실제로 크기를
   * 만지는 자리가 사진 노드이기 때문이다. 속성 패널이 "지금 선택한 노드가 이 경고와 관련 있나"를
   * 판정할 때 단위 소유자뿐 아니라 사진을 골랐을 때도 걸려야 한다.
   */
  imageNodeIds?: string[];
}

/** 종류별 묶음 제목 — 배지 내역의 소제목. 문구는 `label`과 겹치지 않게 **범주**만 말한다. */
export const TEMPLATE_ISSUE_KIND_LABEL: Record<TemplateIssueKind, string> = {
  'param-missing-required': '필수 조회 파라미터 미지정',
  'param-empty-binding': '조회 파라미터 지정값 비어 있음',
  'param-dangling-variable': '없는 변수를 가리키는 지정',
  'param-unknown': '선언에 없는 파라미터 지정',
  'catalog-unknown-entry': '목록에 없는 시스템 조회',
  'catalog-orphan-binding': '참조 없는 조회 파라미터 지정',
  'variable-unused': '참조 없는 전역 변수',
  'variable-key-duplicate': '전역 변수 key 중복',
  'variable-key-invalid': '전역 변수 key 형식 오류',
  'binding-key-duplicate': '식별 키 중복',
  'binding-key-shared-intent-gap': '「의도된 공유」 선언 누락',
  'write-tree-missing': '작성 트리 없음 — 작성폼이 본문 바인딩 파생',
  'layout-overflow-certain': '항상 페이지를 넘치는 구조',
  'layout-overflow-possible': '넘칠 수 있는 이미지 크기',
};

/**
 * 배지 내역의 표시 순서 — **고칠 급함** 순.
 *
 * 값이 실제로 비는 것(필수 미지정·빈 값·없는 변수)이 앞이고, 정리 대상(유령 지정·미사용 변수)이
 * 뒤다. Object 순회 순서에 기대면 종류를 추가할 때 순서가 조용히 바뀐다.
 */
const ISSUE_KIND_ORDER: TemplateIssueKind[] = [
  // 맨 앞 — 값이 비는 것보다 급하다. 이건 **저장하면 반드시** 어긋난 출력물이 나오고(데이터와
  // 무관하게 넘친다), 그 결과가 PDF 낱장 축소라 최종 사용자가 손쓸 수단이 없다.
  'layout-overflow-certain',
  'param-missing-required',
  'param-dangling-variable',
  'param-empty-binding',
  'variable-key-duplicate',
  'variable-key-invalid',
  'binding-key-duplicate',
  // 무선언 중복 바로 뒤 — 같은 "값이 섞인다" 축인데, 저작자가 그 키를 이미 한 번 판단했다는
  // 점만 다르다(새로 늘어난 자리를 마저 보라는 요구).
  'binding-key-shared-intent-gap',
  // 「값이 비는」 묶음 뒤 · 「정리 대상」(유령 지정·미사용 변수) 앞. 맨 앞에 두지 않는 이유는
  // **저작 중에는 정상 상태**이기 때문이다(트리를 아직 안 만든 양식 — 이 파일의 「미완성 양식을
  // 저장해 두고 이어서 저작하는 것이 정상 흐름」 정책 그대로). 그래도 정리 대상보다는 앞인데,
  // 그 셋과 달리 **사용자 화면에 보이는 결과**를 바꾼다(라벨이 식별 키로 뜬다).
  'write-tree-missing',
  'layout-overflow-possible',
  'param-unknown',
  'catalog-unknown-entry',
  'catalog-orphan-binding',
  'variable-unused',
];

/** 조회 표시명 — 없으면 key 그대로(조망이 조용히 비는 것보다 낫다. variableRefs와 같은 규율). */
function catalogLabel(row: CatalogParamRow): string {
  return row.label ?? row.catalogKey;
}

/**
 * 조회 파라미터 지정 축의 경고 — `buildCatalogParamRows`가 이미 낸 판정을 **읽기만** 한다.
 *
 * 행 산출이 unknownParam(선언에 없는데 지정만 남음)·unknownEntry(조회 자체가 목록에 없음)까지
 * 이미 포함하므로, 여기서 다시 `catalogParams`를 훑지 않는다.
 */
function collectParamIssues(rows: CatalogParamRow[]): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  for (const row of rows) {
    const shown = catalogLabel(row);
    if (row.unknownEntry) {
      issues.push({
        kind: 'catalog-unknown-entry',
        severity: 'warn',
        target: 'catalog-params',
        catalogKey: row.catalogKey,
        label: `${withTopicParticle(`'${shown}'`)} 시스템 조회 목록에 없습니다 — 저장된 지정만 남아 있습니다.`,
      });
    }
    for (const item of row.params) {
      const name = item.param.label || item.param.name;
      const base = {
        severity: 'warn',
        target: 'catalog-params',
        catalogKey: row.catalogKey,
        paramName: item.param.name,
      } as const;
      if (item.missingRequired) {
        issues.push({
          ...base,
          kind: 'param-missing-required',
          label: `'${shown}'의 ${withTopicParticle(`'${name}'`)} 폴백이 없는 필수 파라미터인데 지정이 없습니다.`,
        });
      }
      if (item.emptyBinding) {
        issues.push({
          ...base,
          kind: 'param-empty-binding',
          label: `'${shown}'의 '${name}' 지정이 빈 값이라 아직 효력이 없습니다.`,
        });
      }
      if (item.danglingVariable) {
        const variableKey = item.binding?.from === 'variable' ? item.binding.key : undefined;
        issues.push({
          ...base,
          kind: 'param-dangling-variable',
          ...(variableKey ? { variableKey } : {}),
          label: `'${shown}'의 ${withSubjectParticle(`'${name}'`)} 이 양식에 없는 변수 ${withObjectParticle(`'${variableKey ?? ''}'`)} 가리킵니다.`,
        });
      }
      if (item.unknownParam) {
        issues.push({
          ...base,
          kind: 'param-unknown',
          label: `${withSubjectParticle(`'${shown}'`)} 선언하지 않은 파라미터 '${item.param.name}'에 지정만 남아 있습니다.`,
        });
      }
    }
  }
  return issues;
}

/**
 * 유령 지정 — 지정은 남았는데 이 양식 어디서도 그 조회를 참조하지 않는다.
 *
 * 판정은 `collectCatalogConsumers`(참조 전수 열거)와의 대조뿐이다. 그 함수가 catalogParams 서브트리를
 * 건너뛰도록 만들어진 이유가 정확히 이 대조라, 여기서 참조를 다시 세지 않는다.
 */
function collectOrphanBindingIssues(
  template: ReportTemplate,
  rows: CatalogParamRow[],
): TemplateIssue[] {
  const consumers = collectCatalogConsumers(template);
  const labelOf = new Map(rows.map((row) => [row.catalogKey, catalogLabel(row)] as const));
  return Object.keys(template.catalogParams ?? {})
    .filter((catalogKey) => (consumers.get(catalogKey) ?? []).length === 0)
    .map((catalogKey) => ({
      kind: 'catalog-orphan-binding' as const,
      severity: 'warn' as const,
      target: 'catalog-params' as const,
      catalogKey,
      label: `${withObjectParticle(`'${labelOf.get(catalogKey) ?? catalogKey}'`)} 참조하는 곳이 없는데 지정만 남아 있습니다 — 해제를 권합니다.`,
    }));
}

/**
 * 전역 변수 축의 경고 — key 형식·중복은 `flatKeyError`와 변수 편집기의 판정을 그대로 쓰고,
 * "참조 없음"은 `collectVariableConsumers`의 총계를 읽는다.
 *
 * 참조 0을 경고로 세는 것은 조회 파라미터의 유령 지정과 같은 성격이다 — 값이 틀린 건 아니지만
 * 저작자가 지운 줄 알았거나 참조를 붙이려다 만 흔적이라, 배포 전에 한 번은 눈에 띄어야 한다.
 */
function collectVariableIssues(
  template: ReportTemplate,
  refs: Map<string, VariableConsumerRef[]>,
): TemplateIssue[] {
  const variables = template.variables ?? [];
  const issues: TemplateIssue[] = [];
  for (let index = 0; index < variables.length; index += 1) {
    const v = variables[index];
    const shown = v.label || v.key;
    const base = { severity: 'warn', target: 'variables', variableKey: v.key } as const;
    const keyError = flatKeyError(v.key);
    if (keyError) {
      issues.push({
        ...base,
        kind: 'variable-key-invalid',
        label: `변수 '${shown}'의 key가 올바르지 않습니다 — ${keyError}`,
      });
    }
    // 중복은 **뒤쪽 선언**에만 붙인다 — 같은 key 두 개에 각각 경고를 달면 한 건을 고쳐도 숫자가
    // 그대로라 배지가 고쳐지지 않는 것처럼 읽힌다.
    if (v.key !== '' && variables.some((other, i) => i < index && other.key === v.key)) {
      issues.push({
        ...base,
        kind: 'variable-key-duplicate',
        label: `변수 key '${v.key}'가 중복 선언됐습니다 — 뒤 선언이 앞 선언을 가립니다.`,
      });
    }
    if (variableRefTotal(refs.get(v.key)) === 0) {
      issues.push({
        ...base,
        kind: 'variable-unused',
        // 왜 경고인지까지 적는다 — 전역 변수는 참조가 0이어도 `deriveWriteForm`이 작성 폼 최상단에
        // 입력란으로 파생하므로, 이 상태는 "사용자가 채우는데 아무 데도 안 쓰이는 칸"이다.
        label: `변수 ${withObjectParticle(`'${shown}'(${v.key})`)} 참조하는 곳이 없습니다 — 작성 폼에 입력란은 뜨지만 값이 쓰이지 않습니다.`,
      });
    }
  }
  return issues;
}

/**
 * 한 자리를 감싸는 반복 스코프 1칸(안→밖). `over`가 그 반복이 도는 행 목록의 키다.
 * `binding`은 repeat 노드 자신의 바인딩 — 카탈로그 배열 필드 소스는 over 키가 아니라 이걸로
 * 역추적된다(`findRepeatSource` 규약).
 */
interface RepeatScopeStep {
  over: string;
  binding?: { catalogKey?: string; select?: string };
}

/**
 * (row,col)을 담는 반복영역 밴드 — 없으면 undefined. 밴드 셀은 그 밴드의 `over` 행 스코프다.
 */
function findRepeatRegion(
  table: TableNode,
  row: number,
  col: number,
): { over: string } | undefined {
  return (table.repeatRegions ?? []).find((region) => {
    const colStart = region.colStart ?? 0;
    const colEnd = region.colEnd ?? table.colCount - 1;
    return row >= region.rowStart && row <= region.rowEnd && col >= colStart && col <= colEnd;
  });
}

/**
 * 자리(ownerId) → 그 자리를 감싸는 반복 스코프 체인(안→밖). 반복 밖이면 빈 배열.
 *
 * 소유자 id 규약은 중복 집계와 같다(`node:<id>` / `cell:<tableId>:<row>:<col>`). 표 셀은 그 표의
 * 체인에 **자기 밴드의 over**를 하나 더 얹는다(밴드 안 셀만) — 밴드 셀의 중첩 노드도 그 체인을 잇는다.
 */
function collectOwnerRepeatChains(nodes: ComposerNode[]): Map<string, RepeatScopeStep[]> {
  const out = new Map<string, RepeatScopeStep[]>();
  const walk = (list: ComposerNode[], chain: RepeatScopeStep[]): void => {
    for (const node of list) {
      out.set(`node:${node.id}`, chain);
      switch (node.kind) {
        case 'group':
        case 'stack':
          walk(node.children, chain);
          break;
        case 'repeat':
          // 안쪽이 앞 — merge 체인에서 안쪽 행이 바깥을 덮는 순서와 같다.
          walk([node.template], [{ over: node.over, binding: node.binding }, ...chain]);
          break;
        case 'branch':
          for (const slot of node.branches) walk(slot.children, chain);
          break;
        case 'table':
          for (const cell of node.cells) {
            const band = findRepeatRegion(node, cell.row, cell.col);
            const cellChain = band ? [{ over: band.over }, ...chain] : chain;
            out.set(`cell:${node.id}:${cell.row}:${cell.col}`, cellChain);
            if (cell.content) walk(cell.content, cellChain);
          }
          break;
        default:
          break;
      }
    }
  };
  walk(nodes, []);
  return out;
}

/**
 * 그 자리의 키가 **행마다 자기 값**인가 — 중복 집계에서 뺄 것인가.
 *
 * ⚠️ "반복 안이면 행마다 자기 값"은 **행 필드일 때만** 참이다. 엔진은 행 객체를 부모 위에 spread
 * 하므로(rc `mergeRepeatScope`: `{...parent, ...row}`), 행 객체에 **그 키가 없으면 부모 값이 그대로
 * 읽힌다.** 즉 반복 안 카탈로그 바인딩처럼 행 필드가 아닌 키는 바깥 자리와 **같은 저장 값을 실제로
 * 공유**하며 동시에 렌더된다 — 그건 중복이 맞다(2026-09-03 tml-basic-title 실측: blanket 제외 탓에
 * 배지가 통째로 침묵했다).
 *
 * 행 형상을 정적으로 알 수 없는 over(선언 없는 통과 배열 — 행 객체가 그대로 들고 있는 `entries`
 * 같은 것)는 **보수적으로 제외**한다. 모르는 형상을 근거로 새 오탐 경고를 만들면, 그 배지 숫자를
 * 아무도 안 믿게 되어 진짜 경고까지 함께 죽는다.
 */
function isRowScopedKey(
  bindingKey: string,
  chain: RepeatScopeStep[],
  sources: RepeatSource[],
): boolean {
  for (const step of chain) {
    const source = findRepeatSource(sources, step);
    if (!source) return true; // 형상 미상 → 보수적 제외
    if (source.fields.some((f) => f.key === bindingKey)) return true; // 행 필드 → 행마다 자기 값
  }
  return false; // 감싸는 반복이 없거나, 어느 행 형상에도 없는 키 → 부모 폴백(값 공유 실재)
}

/**
 * 식별 키 중복 — 서로 다른 노드/셀이 같은 `bindingKey`로 **같은 저장 칸**을 쓴다.
 *
 * ⚠️ 여기만 기존 판정을 그대로 재사용하지 못한다. EditorPage의 `collectUsedBindingKeys`는 "편집 중인
 * 소유자를 제외한 나머지가 쓰는 키 집합"이라 한 자리 기준이고, 문서 전체의 중복 쌍을 세는 형태가
 * 아니다. 그래서 순회 자체는 같은 원시 함수(rc `walkBindings`)로 하되, 소유자별 묶음만 여기서 만든다.
 * 슬롯 경로도 마찬가지로 패널 쪽과 같은 재료(`collectOwnerSlotPaths`)를 쓴다 — 판정의 **재료**는
 * 공유하고, 문서 전체를 세는 **방식**만 여기서 정한다.
 *
 * 제외 규칙 셋 — 전부 "값이 실제로 섞이지 않는" 경우다(경고가 오탐이면 배지 숫자를 아무도 안 믿는다):
 *  ① 라디오 그룹 멤버(`checkedValue` 있는 checkbox) — 같은 키 공유가 상호배타의 구현이다.
 *    `collectUsedBindingKeys`의 같은 제외와 짝을 맞춘다.
 *  ② 반복 스코프의 **행 필드**(repeat 노드 template 안 / 표 반복영역 밴드 안에서, 그 반복이 도는
 *    행 형상에 실제로 있는 키) — 행마다 자기 값이다. ⚠️ "반복 안"이라는 위치만으로는 부족하다:
 *    엔진이 행 객체를 부모 위에 spread하므로(`mergeRepeatScope`) 행에 없는 키는 부모 값으로
 *    떨어져 바깥 자리와 값을 **공유**한다. 판정은 `isRowScopedKey`(형상 미상 over는 보수적 제외).
 *  ③ 읽기 경로(compute 입력·branch 조건 바인딩) — 저장 칸을 **소유**하지 않고 남의 값을 읽을 뿐이라,
 *    이걸 중복으로 세면 계산 셀을 쓸 때마다 경고가 뜬다.
 *  ④ 상호배타 슬롯(같은 branch의 다른 슬롯) — 동시에 렌더되지 않으므로 같은 필드의 레이아웃
 *    변형이다. 여기서는 자리를 빼는 게 아니라 **세는 단위를 바꿔** 반영한다(아래).
 *
 * ④의 개수 의미 — "동시에 표시될 수 있는 자리의 최대 묶음" 크기다. 배타 관계는 **비추이적**이라
 * 자리를 하나씩 빼는 방식으로는 셀 수 없다: 중첩 분기의 두 자리는 서로 배타지만 바깥 슬롯의 자리와는
 * 둘 다 함께 뜬다. 그래서 슬롯이 갈리는 branch를 하나 골라 슬롯별 세계로 나눠 재귀하고 최댓값을
 * 취한다(maxCoRenderableOwners). 묶음이 1이면 어느 조합도 함께 뜨지 않는다는 뜻이라 이슈 자체가
 * 없다. 예: tml-basic-title은 4곳(표지·다회차 본문·단일회차 본문·0건 폴백 판)이지만 다회차 슬롯의
 * 2곳만 함께 뜨므로 "2곳"으로 뜬다.
 *
 * ── 「의도된 공유」 선언(`binding.sharedIntent`)이 묶음을 읽는 방식 ─────────────────────────
 * 값이 섞이는 것이 **맞고** 저작자가 그걸 안다고 그 자리에 적어 둔 표시다. 여기서는 자리를 셈에서
 * 빼지 않고 **묶음 안에서 선언된 자리(M)와 안 된 자리(U)를 나눠 센다**:
 *  · U ≥ 2, M = 0 → 종전 중복 경고. 아무도 선언하지 않은 평범한 충돌이다.
 *  · M ≥ 1, U ≥ 1 → `binding-key-shared-intent-gap`. **이 규칙이 핵심이다** — 전부 선언해 조용해진
 *    키에 나중에 새 자리가 붙으면 그 자리만 선언이 없으므로 즉시 다시 걸린다.
 *  · U = 0(묶음 전부 선언) 또는 묶음 크기 1 → 조용.
 *
 * 규약은 **"경고에 뜬 자리를 전부 체크"** 이고 "정본 한 자리만 남긴다"가 아니다. 어느 자리가 정본인지는
 * 기계가 판정할 수 없는 저작자 판단이라, 그걸 규약으로 삼으면 저작자마다 다르게 남긴다.
 *
 * M ≥ 1이면 U가 2 이상이어도 gap 쪽으로 보낸다(개수는 U). 두 규칙이 겹치는 구간인데, 종전 문구는
 * 묶음 전체 크기를 말하므로 체크를 해도 숫자가 안 움직여 **체크박스가 고장난 것처럼 읽힌다**. U를
 * 세면 하나 체크할 때마다 숫자가 줄어 조용해질 때까지 진척이 보인다.
 *
 * 자리별 선언인 것이 핵심이다 — 키 단위 무시 목록이면 새 자리가 늘어도 경고가 영영 침묵해 진짜
 * 실수를 덮는다. 규약 정본은 shared `Binding.sharedIntent` doc.
 */
function collectBindingKeyDuplicates(
  template: ReportTemplate,
  catalog: CatalogEntry[] | undefined,
): TemplateIssue[] {
  const body = template.body ?? [];
  const repeatChains = collectOwnerRepeatChains(body);
  // 행 필드 목록은 저작 UI가 "반복 항목 필드" 후보로 쓰는 것과 **같은 파생**이다 — 여기서 따로
  // 만들면 배지가 저작자에게 보이지 않는 기준으로 경고하게 된다.
  const repeatSources = collectRepeatSources(
    template.dataDefs,
    catalog ?? [],
    template.dataSources,
  );
  const slotPaths = collectOwnerSlotPaths(body);
  // bindingKey → 소유자 목록(자리 단위 중복 제거, 트리 순서). 같은 소유자가 여러 번 방문돼도 한 번만.
  const owners = new Map<string, DuplicateOwner[]>();

  walkBindings(body, (binding, owner) => {
    const key = binding.bindingKey;
    if (!key) return;
    if (owner.source !== 'node' && owner.source !== 'cell') return;
    if (owner.node?.kind === 'checkbox' && owner.node.checkedValue) return;
    const ownerNodeId = owner.cell?.tableId ?? owner.nodeId;
    if (!ownerNodeId) return;
    const ownerId = owner.cell
      ? `cell:${owner.cell.tableId}:${owner.cell.row}:${owner.cell.col}`
      : `node:${owner.nodeId}`;
    if (isRowScopedKey(key, repeatChains.get(ownerId) ?? [], repeatSources)) return;
    const list = owners.get(key) ?? [];
    if (!list.some((o) => o.ownerId === ownerId)) {
      list.push({
        ownerId,
        nodeId: ownerNodeId,
        slotPath: slotPaths.get(ownerId) ?? [],
        sharedIntent: binding.sharedIntent === true,
      });
    }
    owners.set(key, list);
  });

  const issues: TemplateIssue[] = [];
  for (const [bindingKey, list] of owners) {
    // 함께 뜰 수 있는 자리만 센다 — 배타 슬롯의 같은 필드는 값이 실제로 섞이지 않는다.
    const group = maxCoRenderableOwners(list);
    if (group.length < 2) continue;
    const undeclared = group.filter((o) => !o.sharedIntent);
    // 묶음 전부가 「의도된 공유」 선언 — 저작자가 이 관계를 이미 다 훑었다.
    if (undeclared.length === 0) continue;
    // 이동 대상은 **그 묶음의** 첫 자리다 — 세지 않은 배타 자리로 데려가면 저작자가 왜 경고가
    // 떴는지 못 읽는다. gap 경고는 손댈 자리가 선언 안 된 쪽이라 그중 첫 자리로 보낸다.
    if (undeclared.length < group.length) {
      issues.push({
        kind: 'binding-key-shared-intent-gap',
        severity: 'warn',
        target: 'canvas',
        bindingKey,
        nodeId: undeclared[0].nodeId,
        label: `식별 키 '${bindingKey}'를 함께 쓰는 자리 중 ${undeclared.length}곳이 「의도된 공유」로 선언되지 않았습니다 — 그 자리도 선언하거나 키를 바꾸세요.`,
      });
      continue;
    }
    issues.push({
      kind: 'binding-key-duplicate',
      severity: 'warn',
      target: 'canvas',
      bindingKey,
      nodeId: group[0].nodeId,
      label: `식별 키 '${bindingKey}'를 동시에 표시될 수 있는 ${group.length}곳이 함께 씁니다 — 같은 저장 값을 공유합니다.`,
    });
  }
  return issues;
}

/** 중복 후보 자리 1개 — 배지가 세는 단위(자리)와 그 자리의 분기 슬롯 경로. */
interface DuplicateOwner {
  ownerId: string;
  /** 이동 대상 노드 id(셀이면 그 표 노드). */
  nodeId: string;
  slotPath: BranchSlotStep[];
  /** 이 자리에 「의도된 공유」 선언(`binding.sharedIntent`)이 붙어 있나. */
  sharedIntent: boolean;
}

/**
 * 동시에 렌더될 수 있는 자리의 **최대 묶음**(트리 순서 보존).
 *
 * 한 조합이 실제로 함께 뜨려면 "각 branch에서 슬롯 하나씩"을 고르는 배치가 존재해야 한다. 그래서
 * 슬롯이 갈리는 branch를 하나 골라 **슬롯마다 하나의 세계**로 나누고(그 branch를 지나지 않는 자리는
 * 어느 세계에나 남는다), 각 세계에서 재귀해 가장 큰 것을 취한다. 갈리는 branch가 없으면 그 목록은
 * 전부 함께 뜬다. 고른 branch는 각 세계 안에서 더 이상 갈리지 않으므로 재귀는 반드시 끝난다.
 *
 * 쌍(pair)만 비교하는 방식으로 대체하지 말 것 — 배타는 비추이적이라(A↔B 배타여도 둘 다 C와는 공존)
 * 쌍 검사로는 "함께 뜨는 최대 묶음"이 안 나온다.
 */
function maxCoRenderableOwners(list: DuplicateOwner[]): DuplicateOwner[] {
  const slotsByBranch = new Map<string, string[]>();
  for (const owner of list) {
    for (const step of owner.slotPath) {
      const slots = slotsByBranch.get(step.branchNodeId) ?? [];
      if (!slots.includes(step.slotId)) slots.push(step.slotId);
      slotsByBranch.set(step.branchNodeId, slots);
    }
  }
  const diverging = [...slotsByBranch.entries()].find(([, slots]) => slots.length > 1);
  if (!diverging) return list;

  const [branchNodeId, slotIds] = diverging;
  let best: DuplicateOwner[] = [];
  for (const slotId of slotIds) {
    const world = list.filter((owner) => {
      const step = owner.slotPath.find((s) => s.branchNodeId === branchNodeId);
      return step === undefined || step.slotId === slotId;
    });
    const got = maxCoRenderableOwners(world);
    if (got.length > best.length) best = got;
  }
  return best;
}

/**
 * 페이지 넘침 축의 경고 — rc `findOversizeUnits`가 이미 낸 판정을 **문구로 옮기기만** 한다.
 *
 * 높이 계산을 여기서 다시 쓰지 않는 이유는 이 파일의 규율 그대로다(위 파일 주석). 판정식이
 * 두 벌이 되면 캔버스 분할(같은 저작값을 보는 `planEditPages`)과 배지가 서로 다른 말을 한다.
 *
 * 처방을 문구에 **값까지 넣어** 적는 것은 결정의 요구다 — 저작자가 손댈 지렛대는 새 옵션이
 * 아니라 기존 높이 필드이고, "몇 px 이하로"까지 나와야 그 자리에서 닫힌다.
 *
 * ⚠️ 그 "값"은 두 가지를 지켜야 실제로 닫힌다.
 *  ① **mm 병기** — 판정·저장은 px인데 에디터의 높이 입력란은 **mm**로 받는다(`HeightEditor`).
 *    px만 적으면 저작자가 그 숫자를 mm 칸에 그대로 넣어 엉뚱한 크기가 된다.
 *  ② **한도는 내림** — 반올림하면 표시값이 실제 한도를 **넘겨** 그대로 입력해도 경고가 안 닫힌다
 *    (실측: 447px을 mm로 반올림한 118.3mm는 447.12px이라 한도 초과. 내림 118.2mm는 446.74px).
 *    넘침량(`overflowBy`)은 처방이 아니라 정보라 반올림해도 무해하다.
 */
function collectOverflowIssues(template: ReportTemplate): TemplateIssue[] {
  const px = (value: number): string => String(Math.round(value));
  /** 처방 한도 — px/mm 모두 내림(위 주석 ②). mm는 입력란 정밀도에 맞춰 소수 1자리. */
  const capPx = (value: number): number => Math.floor(value);
  const capMm = (value: number): number => Math.floor((value / MM_TO_PX) * 10) / 10;
  return findOversizeUnits(template).map((finding: OversizeFinding) => {
    const unit = finding.unit === 'repeat-item' ? '반복 항목' : '항목';
    const base = {
      severity: 'warn' as const,
      target: 'canvas' as const,
      nodeId: finding.nodeId,
      imageNodeIds: finding.imageNodeIds,
    };
    if (finding.verdict === 'certain') {
      const over = px(finding.overflowBy ?? 0);
      const how =
        finding.suggestedImageHeight === undefined
          ? '구성을 나누거나 크기를 줄이세요.'
          : `사진 높이를 ${capPx(finding.suggestedImageHeight)}px(약 ${capMm(finding.suggestedImageHeight)}mm) 이하로 낮추세요.`;
      return {
        ...base,
        kind: 'layout-overflow-certain' as const,
        // 조사 「은」은 `unit`의 두 값(반복 항목·항목)이 모두 받침으로 끝나서다
        label: `지정한 크기대로면 이 ${unit}은 페이지를 ${over}px 넘칩니다 — ${how}`,
      };
    }
    return {
      ...base,
      kind: 'layout-overflow-possible' as const,
      label:
        '이미지 높이 상한이 없어 원본 크기에 따라 페이지를 넘칠 수 있습니다 — 높이 상한을 지정하세요.',
    };
  });
}

/**
 * 이 양식의 저작 경고 전부 — 저장 버튼 옆 배지의 단일 원료.
 *
 * 순서는 `ISSUE_KIND_ORDER`(고칠 급함 순)로 안정화한다 — 내부 순회 순서를 그대로 내면 같은 문서인데
 * 편집 한 번에 목록 순서가 흔들린다.
 */
/**
 * 작성 트리 부재 경고 — 「작성폼이 저작 트리가 아니라 **본문 바인딩 파생**으로 그려지는 상태」.
 *
 * ⚠️ **판정을 여기서 다시 쓰지 않는다** — 파생의 정본은 rc `deriveWriteForm`이고, 세 작성 surface
 * (web `new-composed` · web `external/composed/[token]` · admin `SimulationPage`)가
 * `writeTree = 저작 트리 ?? 파생`으로 **같은 선택**을 한다(2026-09-30 S3-b). 여기서는 그 함수를
 * **런타임과 같은 방식으로** 불러 결과만 센다.
 *
 * ⭐ **카탈로그를 넘기지 않는 것은 런타임과 같은 «호출 형태»를 지키는 것이고, 오늘의 탐지기는
 * 아니다.** 판정도 문구도 카탈로그 유무에 영향받지 않는다 — 파생 개수는
 * `createWriteFormBindingFilter`가 `template.body`만 읽어 정하고(카탈로그 무관), 아래 문구는
 * 노드의 `label`이 아니라 `bindingKey`를 읽는다. 그래도 넘기지 않는 이유는, 넘기는 순간
 * `scaffoldFromBinding`이 `entry.label`·`required`·widget을 해소한 노드를 보게 되어
 * **「런타임이 보는 것」과 다른 것을 재기 시작하는 입구**가 되기 때문이다(세 surface는 전부
 * 카탈로그 없이 부른다). ⇒ 이 한 줄의 초록을 「배지가 런타임과 같다」의 근거로 인용하지 말 것.
 *
 * ⭐ **선언에서 라벨을 받는 파생 채널(전역 변수 · 非compute dataDef)은 세지 않는다.** 전역 변수는
 * `label: v.label`·widget·options·defaultValue를 선언에서 그대로 받아 **온전하고**(required 축만
 * 없는데 `TemplateVariable`에 그 축 자체가 없다), `dataDefs`도 똑같이 `label: def.label`을 받는다
 * (rc `writeForm.ts` dataDef 루프 — `label: string`이 **필수 필드**라 비는 경우가 없다. `compute`
 * def는 애초에 파생되지 않는다). 라벨이 식별 키로 떨어지는 것은 **본문 바인딩 파생**뿐이므로
 * (`scaffoldFromBinding`의 `label: binding.bindingKey` 기본값) 그 차집합만 센다.
 *
 * ⭐ **dataDef를 «키 차집합»으로 빼지 않고 `dataDefs: []`로 다시 부르는 이유** — rc 파생 순서가
 * 「변수 → 본문 바인딩 → dataDef」이고 `seen`이 먼저 온 쪽을 남기므로, 본문 바인딩이 dataDef와
 * **같은 키를 선점하면 그 노드는 본문 파생**이다(dataDef 루프는 `seen.has(def.key)`로 건너뛴다).
 * 키로 빼면 그 경우까지 함께 빠져 **반대 방향으로 과소 집계**가 된다. 전역 변수는 파생 순서가
 * 본문 바인딩보다 **앞**이라 키 차집합이 정확하다 — 두 채널의 처리가 다른 것은 그 순서 때문이다.
 * (판정을 여기서 다시 쓰지 않는다는 위 규율은 그대로다 — 세는 것은 여전히 rc 함수의 산출이고,
 * 넘기지 않는 것은 **채널 하나**일 뿐 `template.body`는 건드리지 않는다.)
 *
 * ⭐ **공개(enabled)된 양식에만 띄운다 — 이것이 이 경고의 핵심 게이트다.** 트리 없음은 **저작 중에는
 * 정상 상태**다(본문을 먼저 짜고 작성 탭을 뒤에 만든다). 처음엔 공개 여부를 안 보게 만들었더니
 * 이 파일의 기존 spec **10건 이상이 red**가 됐는데, 그 픽스처들이 전부 「다른 축을 재려고 만든
 * 부분 템플릿(본문만 있고 트리 없음)」이었다 — 즉 **실제 저작 중 상태와 같은 모양**이라, 그대로
 * 두면 양식을 짜는 내내 배지가 울려 **경고가 배경음이 된다.** 공개 스위치는 「이 양식을 일반
 * 사용자가 쓴다」는 선언이므로, 그 순간이 이 상태가 **위험해지는 시점**이고 공개 토글이 같은 화면에
 * 있어 저작자가 **켠 직후 바로** 본다.
 *
 * ⭐ **본문 입력이 0이면 경고하지 않는다** — 트리도 본문도 없으면 파생도 비어 작성폼 자체가
 * 그려지지 않으니(렌더 게이트) 이 경고가 말할 것이 없다.
 *
 * 왜 이 경고가 있나(2026-10-03): 파생 경로의 required·라벨을 **기능으로** 메우는 두 안(파생에
 * 카탈로그 넘기기 / 파생에 required 선언 축 신설)은 그 경로를 밟는 **활성 템플릿이 0건**이라
 * 보류했고(prod 45행 중 enabled 6 · 무트리 enabled **0**), 대신 **그 상태가 만들어지는 순간**을
 * 저작자에게 알리는 쪽을 택했다. 무트리 18건이 비활성으로 남아 있고 **전부 본문이 비어 있지
 * 않으므로**(enable 하면 즉시 이 경로가 열린다) 「지금 0건」은 안전 근거가 아니라 유예다.
 * 결정 기록: `docs/decisions/2026-10-03-measure-the-wiring-not-the-blocking.md`.
 */
function collectWriteTreeIssues(
  template: ReportTemplate,
  enabled: boolean | undefined,
): TemplateIssue[] {
  if (!enabled) return [];
  if (template.composerWidgetTree?.length) return [];
  // 두 선언 채널을 **다르게** 다루는 이유는 rc 파생 순서 하나다(변수 → 본문 바인딩 → dataDef):
  // 변수는 먼저라 그 키가 언제나 변수 노드를 내므로 키 차집합이 정확하고, dataDef는 마지막이라
  // 본문 바인딩이 같은 키를 선점하면 그 노드가 **본문 파생**이어서 키로 빼면 과소 집계가 된다.
  // 그래서 dataDef는 키가 아니라 **채널을 비워** 부른다. (상세는 위 JSDoc ⭐)
  const variableKeys = new Set((template.variables ?? []).map((v) => v.key));
  const fromBindings = deriveWriteForm({ ...template, dataDefs: [] }).filter(
    (n) => !variableKeys.has(n.bindingKey),
  );
  if (fromBindings.length === 0) return [];
  return [
    {
      kind: 'write-tree-missing',
      severity: 'warn',
      label:
        `작성 탭이 비어 있어 본문 바인딩에서 입력칸 ${fromBindings.length}개가 자동 파생된다 — ` +
        `라벨이 식별 키로 뜨고(예: ${fromBindings[0].bindingKey}) 필수 검증이 걸리지 않는다`,
      target: 'write-canvas',
    },
  ];
}

export function collectTemplateIssues(
  template: ReportTemplate,
  catalog: CatalogEntry[] | undefined,
  /**
   * 이미 계산해 둔 변수 참조 조망 — 편집기는 변수 편집기용으로 같은 것을 매 편집마다 만들고 있어,
   * 넘겨주면 전수 순회 한 벌을 아낀다. 없으면 여기서 만든다(테스트·다른 호출부가 그대로 쓴다).
   */
  variableRefs?: Map<string, VariableConsumerRef[]>,
  /**
   * 공개(enabled) 상태 — `ReportTemplate`에 없는 축이라 호스트가 넘긴다(`StoredReportTemplate`의
   * 필드이고 에디터는 편집 funnel 밖 state로 따로 들고 있다). **생략=비공개로 본다** —
   * 비공개 양식에는 `write-tree-missing`을 띄우지 않는 것이 정책이다(그 함수 JSDoc 참조).
   */
  enabled?: boolean,
): TemplateIssue[] {
  const rows = buildCatalogParamRows(template, catalog);
  const issues = [
    ...collectParamIssues(rows),
    ...collectOrphanBindingIssues(template, rows),
    ...collectVariableIssues(template, variableRefs ?? collectVariableConsumers(template)),
    ...collectBindingKeyDuplicates(template, catalog),
    ...collectWriteTreeIssues(template, enabled),
    ...collectOverflowIssues(template),
  ];
  return issues.sort(
    (a, b) => ISSUE_KIND_ORDER.indexOf(a.kind) - ISSUE_KIND_ORDER.indexOf(b.kind),
  );
}

/** 배지 내역의 한 묶음 — 종류별 합산. */
export interface TemplateIssueGroup {
  kind: TemplateIssueKind;
  /** 종류 제목(`TEMPLATE_ISSUE_KIND_LABEL`). */
  title: string;
  issues: TemplateIssue[];
}

/** 종류별로 묶은 내역 — 빈 종류는 만들지 않는다. 순서는 `collectTemplateIssues`의 정렬을 잇는다. */
export function groupTemplateIssues(issues: TemplateIssue[]): TemplateIssueGroup[] {
  const byKind = new Map<TemplateIssueKind, TemplateIssue[]>();
  for (const issue of issues) {
    const list = byKind.get(issue.kind) ?? [];
    list.push(issue);
    byKind.set(issue.kind, list);
  }
  return [...byKind.entries()].map(([kind, list]) => ({
    kind,
    title: TEMPLATE_ISSUE_KIND_LABEL[kind],
    issues: list,
  }));
}
